import { useMemo, useState } from "react";

type PortalShop = {
  id: string;
  name: string;
  slug: string;
  isNewCustomerPortalEnabled?: boolean | null;
  hasExchanges?: boolean | null;
  hasExchangeCredit?: boolean | null;
  hasGiftCode?: boolean | null;
};

function BoolBadge({ value, label }: { value: boolean; label: string }) {
  return (
    <span
      title={label}
      className={[
        "inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold",
        value ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-400",
      ].join(" ")}
    >
      {value ? "✓" : "–"}
    </span>
  );
}

function PortalBadge({ isNew }: { isNew: boolean }) {
  return (
    <span
      className={[
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
        isNew ? "bg-violet-50 text-violet-600" : "bg-orange-50 text-orange-600",
      ].join(" ")}
    >
      {isNew ? "Nouveau" : "Ancien"}
    </span>
  );
}

type TriState = "all" | "on" | "off";

function nextTriState(state: TriState): TriState {
  if (state === "all") return "on";
  if (state === "on") return "off";
  return "all";
}

function FeatureFilterButton({
  label,
  state,
  onClick,
}: {
  label: string;
  state: TriState;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        "rounded-md border bg-background px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted/60",
        state === "on"
          ? "border-emerald-400 text-emerald-600 ring-1 ring-emerald-200"
          : state === "off"
            ? "border-gray-400 text-gray-600 ring-1 ring-gray-200"
            : "",
      ].join(" ")}
    >
      {label}
      {state !== "all" && (
        <span className="ml-1 text-xs">{state === "on" ? "✓" : "✕"}</span>
      )}
    </button>
  );
}

export function CustomerPortalTab({ shops }: { shops: PortalShop[] }) {
  const [search, setSearch] = useState("");
  const [portalFilter, setPortalFilter] = useState<"all" | "new" | "old">(
    "all",
  );
  const [exchangesFilter, setExchangesFilter] = useState<TriState>("all");
  const [exchangeCreditFilter, setExchangeCreditFilter] =
    useState<TriState>("all");
  const [giftCodeFilter, setGiftCodeFilter] = useState<TriState>("all");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return shops.filter((shop) => {
      if (
        q &&
        !shop.name.toLowerCase().includes(q) &&
        !shop.slug.toLowerCase().includes(q)
      ) {
        return false;
      }
      const isNew = shop.isNewCustomerPortalEnabled ?? false;
      if (portalFilter === "new" && !isNew) return false;
      if (portalFilter === "old" && isNew) return false;

      const hasExchanges = shop.hasExchanges ?? false;
      if (exchangesFilter === "on" && !hasExchanges) return false;
      if (exchangesFilter === "off" && hasExchanges) return false;

      const hasExchangeCredit = shop.hasExchangeCredit ?? false;
      if (exchangeCreditFilter === "on" && !hasExchangeCredit) return false;
      if (exchangeCreditFilter === "off" && hasExchangeCredit) return false;

      const hasGiftCode = shop.hasGiftCode ?? false;
      if (giftCodeFilter === "on" && !hasGiftCode) return false;
      if (giftCodeFilter === "off" && hasGiftCode) return false;

      return true;
    });
  }, [
    shops,
    search,
    portalFilter,
    exchangesFilter,
    exchangeCreditFilter,
    giftCodeFilter,
  ]);

  const newCount = shops.filter((s) => s.isNewCustomerPortalEnabled).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center rounded-md border bg-background px-3 py-1.5 text-sm font-medium">
          <span className="font-semibold text-violet-600">{newCount}</span>
          <span className="ml-1 text-muted-foreground">
            / {shops.length} shops sur le nouveau portail
          </span>
        </div>

        <div className="w-px h-5 bg-border" />

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un shop…"
          className="w-64 rounded-md border bg-background px-3 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500"
        />

        {(
          [
            { key: "all", label: "Tous" },
            { key: "new", label: "Nouveau portail" },
            { key: "old", label: "Ancien portail" },
          ] as const
        ).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setPortalFilter(key)}
            className={[
              "rounded-md border bg-background px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted/60",
              portalFilter === key
                ? "border-violet-400 text-violet-600 ring-1 ring-violet-200"
                : "",
            ].join(" ")}
          >
            {label}
          </button>
        ))}

        <div className="w-px h-5 bg-border" />

        <FeatureFilterButton
          label="Échanges"
          state={exchangesFilter}
          onClick={() => setExchangesFilter(nextTriState(exchangesFilter))}
        />
        <FeatureFilterButton
          label="Échanges crédit"
          state={exchangeCreditFilter}
          onClick={() =>
            setExchangeCreditFilter(nextTriState(exchangeCreditFilter))
          }
        />
        <FeatureFilterButton
          label="Bons d'achat"
          state={giftCodeFilter}
          onClick={() => setGiftCodeFilter(nextTriState(giftCodeFilter))}
        />
      </div>

      <div className="overflow-x-auto rounded-lg border bg-background">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2">Shop</th>
              <th className="px-3 py-2">Portail client</th>
              <th className="px-3 py-2 text-center">Échanges</th>
              <th className="px-3 py-2 text-center">Échanges crédit</th>
              <th className="px-3 py-2 text-center">Bons d&apos;achat</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((shop) => (
              <tr
                key={shop.id}
                className="border-b last:border-0 hover:bg-muted/30"
              >
                <td className="px-3 py-2">
                  <div className="font-medium">{shop.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {shop.slug}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <PortalBadge
                    isNew={shop.isNewCustomerPortalEnabled ?? false}
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <BoolBadge
                    value={shop.hasExchanges ?? false}
                    label="Échanges"
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <BoolBadge
                    value={shop.hasExchangeCredit ?? false}
                    label="Échanges crédit"
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <BoolBadge
                    value={shop.hasGiftCode ?? false}
                    label="Bons d'achat"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground">
            Aucun shop ne correspond aux filtres.
          </div>
        )}
      </div>
    </div>
  );
}
